import { useEffect, useState } from 'react'
import { ZoomIn } from 'lucide-react'

interface ImageWithDescriptionBlockProps {
  block: {
    media?: {
      url: string
      alternativeText?: string | null
      name?: string
    }
    description?: unknown[]
  }
}

const getCaptionText = (description?: unknown[]) => {
  if (!Array.isArray(description)) return ''

  return description
    .flatMap((node: any) => node.children ?? [])
    .map((child: any) => child.text ?? '')
    .join('')
}

export const ImageWithDescriptionBlock = ({ block }: ImageWithDescriptionBlockProps) => {
  if (!block.media) return null

  const caption = getCaptionText(block.description)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const imageAlt = block.media.alternativeText || block.media.name || 'Article image'

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      setIsOpen(true)
    }
  }

  return (
    <figure className="py-6">
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        onKeyDown={handleKeyDown}
        aria-haspopup="dialog"
        aria-label={imageAlt}
        className="group relative mx-auto block h-[200px] w-full max-w-[600px] cursor-zoom-in overflow-hidden rounded-lg bg-white p-0 text-left outline-none transition-shadow hover:shadow-md focus-visible:shadow-lg sm:h-[260px] md:h-[350px]"
      >
        <img
          src={block.media.url}
          alt={imageAlt}
          title={block.media.name}
          className="h-full w-full object-cover object-center"
        />

        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background/90 text-foreground shadow-lg">
            <ZoomIn className="h-7 w-7" aria-hidden="true" />
          </span>
        </span>
      </button>

      {caption && (
        <figcaption className="pt-3 text-sm leading-6 text-center text-muted-foreground italic">
          {caption}
        </figcaption>
      )}

      {isOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={imageAlt}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center bg-black/80 p-[2vh_2vw]"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="relative flex h-[85vh] w-[85vw] cursor-default flex-col items-center justify-center rounded-2xl bg-white p-3 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="Close image"
              onClick={() => setIsOpen(false)}
              className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-border bg-background text-2xl leading-none text-foreground shadow-md hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              ×
            </button>

            <img
              src={block.media.url}
              alt={imageAlt}
              title={block.media.name}
              className="h-full max-h-[calc(85vh-70px)] w-full max-w-[calc(85vw-24px)] object-contain"
            />

            {caption && (
              <figcaption className="mt-3 max-w-[calc(85vw-32px)] text-center text-sm italic leading-6 text-muted-foreground">
                {caption}
              </figcaption>
            )}
          </div>
        </div>
      )}
    </figure>
  )
}

export default ImageWithDescriptionBlock
